import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BookOpen, Lightbulb, Shield, ArrowRight, Brain, Target } from "lucide-react";
import { FALLACY_DEFINITIONS, type FallacyDefinition } from "@/constants/fallacyDefinitions";
import { getConfidenceColor } from "@/utils/ui/confidenceHelpers";
import { getSeverityColor, getCategoryColor } from "@/utils/ui/fallacyColors";
import { getSimilarFallacies, getExamplePreview } from "@/utils/data/fallacyHelpers";
import { UI_TEXT } from "@/constants/ui";
import type { FallacyData } from "@/types/flipped";

interface FallacyDetailModalProps {
  fallacyType: string | null;
  fallacyData?: FallacyData | null;
  isOpen: boolean;
  onClose: () => void;
}

export const FallacyDetailModal = ({ fallacyType, fallacyData, isOpen, onClose }: FallacyDetailModalProps) => {
  const [activeTab, setActiveTab] = useState<"ai-analysis" | "definition" | "examples" | "similar">("ai-analysis");
  
  if (!fallacyType || !FALLACY_DEFINITIONS[fallacyType]) {
    return null;
  }

  const fallacy = FALLACY_DEFINITIONS[fallacyType];
  const similarFallacies = getSimilarFallacies(fallacyType);
  const examplePreview = getExamplePreview(fallacy.examples);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-2 sm:space-y-3">
              <DialogTitle className="text-xl sm:text-2xl font-bold text-slate-800 leading-tight">
                {fallacy.name}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${getSeverityColor(fallacy.severity)} text-white text-xs`}>
                  {fallacy.severity} {UI_TEXT.labels.severity}
                </Badge>
                <Badge className={`${getCategoryColor(fallacy.category)} text-white text-xs`}>
                  {fallacy.category}
                </Badge>
                {fallacyData && (
                  <Badge variant="outline" className="border-blue-500 text-blue-700 text-xs">
                    {fallacyData.confidence}% {UI_TEXT.labels.confidence}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Navigation Tabs */}
          <div className="flex flex-wrap gap-1 sm:gap-2 border-b pb-2">
            {fallacyData && (
              <Button
                variant={activeTab === "ai-analysis" ? "default" : "ghost"}
                onClick={() => setActiveTab("ai-analysis")}
                className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 h-auto"
              >
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">AI Analysis</span>
                <span className="xs:hidden">AI</span>
              </Button>
            )}
            <Button
              variant={activeTab === "definition" ? "default" : "ghost"}
              onClick={() => setActiveTab("definition")}
              className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 h-auto"
            >
              <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Definition</span>
              <span className="xs:hidden">Info</span>
            </Button>
            <Button
              variant={activeTab === "examples" ? "default" : "ghost"}
              onClick={() => setActiveTab("examples")}
              className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 h-auto"
            >
              <Lightbulb className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Examples</span>
              <span className="xs:hidden">Ex</span>
            </Button>
            <Button
              variant={activeTab === "similar" ? "default" : "ghost"}
              onClick={() => setActiveTab("similar")}
              className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 h-auto"
            >
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Similar</span>
              <span className="sm:hidden">Sim</span>
            </Button>
          </div>

          {/* Tab Content */}
          {activeTab === "ai-analysis" && fallacyData && (
            <div className="space-y-4">
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    Why This Was Detected in Your Text
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4">
                    <span className="text-sm font-medium text-slate-600">Detection Confidence:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getConfidenceColor(fallacyData.confidence)} transition-all duration-500`}
                          style={{ width: `${fallacyData.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700">
                        {fallacyData.confidence}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-slate-800 mb-2">AI's Explanation:</h4>
                      <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                        {fallacyData.explanation}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-slate-800 mb-2">Detailed Reasoning:</h4>
                      <p className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                        {fallacyData.reasoning}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "definition" && (
            <div className="flex flex-col gap-4 min-h-[400px]">
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      Understanding {fallacy.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      {fallacy.description}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                      How to Counter This Fallacy
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      {fallacy.antidote}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {fallacy.alternativeNames.length > 0 && (
                <Card className="mt-auto">
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="text-lg sm:text-xl">Also Known As</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {fallacy.alternativeNames.map((name, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === "examples" && (
            <div className="max-w-2xl mx-auto space-y-4">
              <h3 className="text-lg sm:text-xl font-semibold text-slate-800 text-center mb-4">
                Examples
              </h3>
              <div className="space-y-3">
                {fallacy.examples.map((example, index) => (
                  <div key={index} className="p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <p className="text-sm sm:text-base text-slate-700 italic">
                      "{example}"
                    </p>
                  </div>
                ))}
              </div>
              <div className="p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Why this reasoning is flawed:</h4>
                <p className="text-xs sm:text-sm text-amber-700">
                  {fallacy.examples.length > 1 ? 'These examples demonstrate' : 'This example demonstrates'} the {fallacy.name.toLowerCase()} by showing how the argument structure fails to support its conclusion logically.
                </p>
              </div>
            </div>
          )}

          {activeTab === "similar" && (
            <div className="space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-slate-800">
                Similar {fallacy.category} Fallacies
              </h3>
              {similarFallacies.length > 0 ? (
                <div className="grid gap-3 sm:gap-4">
                  {similarFallacies.map(([key, similarFallacy]) => (
                    <Card key={key} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                          <h4 className="font-medium text-slate-800 text-sm sm:text-base">{similarFallacy.name}</h4>
                          <Badge className={`${getSeverityColor(similarFallacy.severity)} text-white text-xs flex-shrink-0`}>
                            {similarFallacy.severity}
                          </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 mb-2">
                          {similarFallacy.description}
                        </p>
                        <p className="text-xs text-slate-500 italic">
                          Example: {similarFallacy.examples[0]}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600">No similar fallacies found in this category.</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
